// Region intelligence panel — opens on region selection. Renders ONLY
// published Commit-6 geographyRadar fields verbatim. Association semantics:
// REGION_LINKED_OBSERVED_ACTIVITY_NOT_ORGANIZATION_DOMICILE — organizations
// are never described as domiciled in / headquartered in the region.
import React from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { DYNAMICS_LABEL } from "./WorldPresenceMap";
import { patternDisplayTitle } from "./TopPatternsTable";

const pct = (v) => (v == null ? "—" : `${(v * 100).toFixed(1)}%`);

const Block = ({ label, children, testId }) => (
    <div data-testid={testId}>
        <div className="text-[9px] uppercase tracking-wider text-[hsl(var(--mute))] mb-1.5">{label}</div>
        {children}
    </div>
);

export const RegionPanel = ({ region, zonesByKey, useCaseLabelByKey, onClose }) => {
    if (!region) return null;
    const dyn = DYNAMICS_LABEL[region.dynamics?.label];
    const cov = region.historyCoverage || {};
    const zoneLabelFor = (z) => zonesByKey?.[z.marketZoneKey]?.label || z.marketZoneLabel;
    const labelForUseCase = (u) => useCaseLabelByKey?.[u.useCaseKey] || u.useCaseLabel;
    return (
        <div className="card-flat p-4 md:p-5" data-testid="wp12-home-region-panel">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <div className="eyebrow">Region</div>
                    <h3 className="text-base font-semibold leading-tight mt-0.5" data-testid="wp12-region-panel-name">{region.label}</h3>
                </div>
                <button type="button" data-testid="wp12-region-panel-close" onClick={onClose}
                        className="p-1 rounded hover:bg-[hsl(var(--surface-sunken))] text-[hsl(var(--mute))]">
                    <X size={14} />
                </button>
            </div>

            <div className="flex items-baseline gap-4 flex-wrap mt-2">
                <div data-testid="wp12-region-panel-share">
                    <span className="num text-xl font-semibold">{pct(region.structuralActivity?.share)}</span>
                    <span className="text-[10px] text-[hsl(var(--mute))] ml-1.5">of observed structural activity</span>
                </div>
                {dyn && (
                    <span className={`text-[11px] font-medium ${dyn[1]}`} data-testid="wp12-region-panel-dynamics">{dyn[0]}</span>
                )}
                <span className="num text-[10px] text-[hsl(var(--mute))]" data-testid="wp12-region-panel-footprint">
                    {region.associatedPatternCount ?? "—"} associated patterns
                </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-x-6 gap-y-4 mt-4">
                <Block label="Most represented market zones" testId="wp12-region-panel-zones">
                    <ul className="space-y-1">
                        {(region.topMarketZones || []).slice(0, 3).map((z) => (
                            <li key={z.marketZoneKey} className="text-[11px] leading-snug">{zoneLabelFor(z)}</li>
                        ))}
                    </ul>
                </Block>

                <Block label="Leading use cases" testId="wp12-region-panel-usecases">
                    <ul className="space-y-1">
                        {(region.topUseCases || []).slice(0, 3).map((u) => (
                            <li key={u.useCaseKey} className="text-[11px] leading-snug">{labelForUseCase(u)}</li>
                        ))}
                    </ul>
                </Block>

                <Block label="Leading patterns" testId="wp12-region-panel-patterns">
                    <ul className="space-y-1">
                        {(region.topRegionalPatterns || []).slice(0, 5).map((p) => (
                            <li key={p.canonicalPatternId} className="text-[11px] leading-snug flex items-baseline justify-between gap-2">
                                <span className="min-w-0">
                                    <Link
                                        to={`/market-v2/patterns/${encodeURIComponent(p.canonicalPatternId)}`}
                                        className="hover:text-[hsl(var(--accent-ink))] hover:underline block truncate"
                                        title={p.displayTitle ? `${p.displayTitle} · ${p.patternLabel}` : p.patternLabel}
                                    >
                                        {patternDisplayTitle(p)}
                                    </Link>
                                    {p.displayTitle && p.patternLabel && p.displayTitle !== p.patternLabel && (
                                        <span className="text-[9px] text-[hsl(var(--mute))] block truncate">{p.patternLabel}</span>
                                    )}
                                </span>
                                <span className="num text-[10px] text-[hsl(var(--mute))] flex-shrink-0">
                                    {p.currentDisplayScoreExact != null ? p.currentDisplayScoreExact.toFixed(1) : "—"}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Block>

                <Block label="Key organizations observed in region-linked activity" testId="wp12-region-panel-orgs">
                    <ul className="space-y-1">
                        {(region.keyOrganizations || []).slice(0, 5).map((o) => (
                            <li key={o.canonicalOrganizationId} className="text-[11px] leading-snug">
                                {o.organizationName}
                            </li>
                        ))}
                    </ul>
                </Block>
            </div>

            <div className="text-[9px] text-[hsl(var(--mute))] mt-4 pt-3 border-t border-[hsl(var(--line))]" data-testid="wp12-region-panel-coverage">
                Region coverage: {cov.observedWeekCount ?? "—"} observed {cov.observedWeekCount === 1 ? "week" : "weeks"} in the window
                {cov.firstObservedWeek ? <> ({cov.firstObservedWeek} → {cov.lastObservedWeek})</> : null}.
                Organization association reflects region-linked observed activity, not organization domicile.
            </div>
        </div>
    );
};
